import { useMemo } from 'react';

import { Tooltip } from 'antd';

import Button from '@/components/shared/Button/Button';
import { Tariff } from '@/lib/types/tariff';
import { Market } from '@addmile/backend-client';
import { CellContext } from '@tanstack/react-table';

import { TariffType } from '../../hooks/useTariffs';

export const useTariffsTable = (
  tariffs: Tariff[],
  tariffType: TariffType,
  onChangeLTV: (tariffId: number, market: Market) => void,
) => {
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'externalId',
        header: 'External ID',
        cell: ({ getValue }: CellContext<Tariff, React.ReactNode>) => <span>{getValue()}</span>,
        size: 330,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ getValue, row }: CellContext<Tariff, React.ReactNode>) => (
          <Tooltip title={row.original.description}>
            <span>{getValue()}</span>
          </Tooltip>
        ),
        size: 200,
      },
      {
        accessorKey: 'regularPrice',
        header: 'Regular Price',
        cell: ({ getValue }: CellContext<Tariff, React.ReactNode>) => <span>{getValue()}</span>,
        size: 100,
      },
      {
        accessorKey: 'discountPrice',
        header: 'Discount Price',
        cell: ({ getValue }: CellContext<Tariff, React.ReactNode>) => (
          <span>{getValue() || 'N/A'}</span>
        ),
        size: 100,
      },

      {
        accessorKey: 'currency',
        header: 'Currency',
        cell: ({ getValue }: CellContext<Tariff, React.ReactNode>) => <span>{getValue()}</span>,
        size: 100,
      },
    ];

    if (tariffType === TariffType.Subscription) {
      baseColumns.push({
        accessorKey: 'provider',
        header: 'Provider',
        cell: ({ getValue }: CellContext<Tariff, React.ReactNode>) => <span>{getValue()}</span>,
        size: 100,
      });

      baseColumns.push(
        ...Object.values(Market).map((market: Market) => ({
          accessorKey: `ltv-${market}`,
          header: `LTV ${market}`,
          cell: ({ row }: CellContext<Tariff, React.ReactNode>) => (
            <div className="flex items-center gap-2 justify-end">
              <span>{row.original.ltvs.find((ltv) => ltv.market === market)?.amount}</span>
              <Button
                isIcon
                prefixIcon={<img alt={'edit'} src={'/icons/dollar.svg'} />}
                variant="transparent"
                className="border-none p-0"
                size="s"
                onClick={() => onChangeLTV(row.original.id, market)}
              />
            </div>
          ),
          size: 100,
        })),
      );
    }

    return baseColumns;
  }, [tariffType, onChangeLTV]);

  return { data: tariffs, columns };
};
